import axios from 'axios';

const API_BASE = window.location.hostname === 'localhost' ? 'http://localhost:3000' : 'https://syncbridge.co.za/api';

const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to all requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Handle 401 errors - try to refresh token
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (!refreshToken) {
          throw new Error('No refresh token');
        }

        const response = await axios.post(`${API_BASE}/auth/refresh`, {
          refreshToken,
        });

        const { accessToken, refreshToken: newRefreshToken } = response.data;
        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('refreshToken', newRefreshToken);

        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        // Clear tokens and redirect to login
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        window.location.href = '/';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

let currentContext = {
  tenantId: null,
  wooConnectionId: null,
  miraklConnectionId: null,
};

export const setClientContext = (tenant, wooConnection, miraklConnection) => {
  currentContext = {
    tenantId: tenant?.id || null,
    wooConnectionId: wooConnection?.id || null,
    miraklConnectionId: miraklConnection?.id || null,
  };
  console.log('Context set:', currentContext);
};

export const getClientContext = () => currentContext;

export const clearClientContext = () => {
  currentContext = {
    tenantId: null,
    wooConnectionId: null,
    miraklConnectionId: null,
  };
};

export const tenantService = {
  getAll: () => api.get('/tenants'),
  getOne: (id) => api.get(`/tenants/${id}`),
  create: (data) => api.post('/tenants', data),
  update: (id, data) => api.patch(`/tenants/${id}`, data),
  delete: (id) => api.delete(`/tenants/${id}`),
};

export const wooCommerceService = {
  create: (data) => api.post('/woocommerce', data),
  test: (data) => api.post('/woocommerce/test', data),
  testConnection: (id) => api.post(`/woocommerce/${id}/test`),
};

export const miraklService = {
  create: (data) => api.post('/mirakl', data),
  test: (data) => api.post('/mirakl/test', data),
  testConnection: (id) => api.post(`/mirakl/${id}/test`),
};

export const syncService = {
  importProducts: (perPage = 50, page = 1) => {
    if (!currentContext.wooConnectionId) {
      return Promise.reject(new Error('No WooCommerce connection selected'));
    }
    return api.post('/sync/import', {
      wooConnectionId: currentContext.wooConnectionId,
      miraklConnectionId: currentContext.miraklConnectionId,
      perPage,
      page,
    });
  },

  getWooCommerceCount: () => {
    if (!currentContext.wooConnectionId) {
      return Promise.reject(new Error('No WooCommerce connection selected'));
    }
    return api.get(`/sync/woo-count/${currentContext.wooConnectionId}`);
  },

  importAllProducts: () => {
    if (!currentContext.wooConnectionId) {
      return Promise.reject(new Error('No WooCommerce connection selected'));
    }
    return api.post('/sync/import-all', {
      wooConnectionId: currentContext.wooConnectionId,
      miraklConnectionId: currentContext.miraklConnectionId,
    });
  },

  syncStatus: () => {
    if (!currentContext.tenantId || !currentContext.miraklConnectionId) {
      return Promise.reject(new Error('No client/marketplace selected'));
    }
    return api.post('/sync/sync-status', {
      tenantId: currentContext.tenantId,
      miraklConnectionId: currentContext.miraklConnectionId,
    });
  },

  getStagedProducts: (status = null, page = 1, perPage = 50) => {
    if (!currentContext.tenantId) {
      return Promise.reject(new Error('No client selected'));
    }
    let url = `/sync/staged/${currentContext.tenantId}?page=${page}&perPage=${perPage}`;
    if (status) url += `&status=${status}`;
    return api.get(url);
  },

  getStagedProduct: (id) => api.get(`/sync/staged/product/${id}`),

  updateStagedProduct: (id, data) => api.patch(`/sync/staged/product/${id}`, data),

  deleteStagedProduct: (id) => api.delete(`/sync/staged/product/${id}`),

  validateProduct: (id) => api.post(`/sync/staged/product/${id}/validate`),

  bulkUpdateStatus: (ids, status) => api.patch('/sync/staged/bulk-status', { ids, status }),

  submitForApproval: (stagedProductIds) => {
    if (!currentContext.miraklConnectionId) {
      return Promise.reject(new Error('No marketplace selected'));
    }
    return api.post('/sync/submit-for-approval', {
      miraklConnectionId: currentContext.miraklConnectionId,
      stagedProductIds,
    });
  },

  publishProducts: (stagedProductIds) => {
    if (!currentContext.miraklConnectionId) {
      return Promise.reject(new Error('No marketplace selected'));
    }
    return api.post('/sync/publish', {
      miraklConnectionId: currentContext.miraklConnectionId,
      stagedProductIds,
    });
  },

  resyncFromWooCommerce: () => {
    if (!currentContext.tenantId || !currentContext.wooConnectionId) {
      return Promise.reject(new Error('No client/WooCommerce selected'));
    }
    return api.post('/sync/resync-from-woo', {
      tenantId: currentContext.tenantId,
      wooConnectionId: currentContext.wooConnectionId,
    });
  },

  getStats: () => {
    if (!currentContext.tenantId) {
      return Promise.reject(new Error('No client selected'));
    }
    return api.get(`/sync/stats/${currentContext.tenantId}`);
  },
};

export default api;

